import type { FC } from 'react';
import React, { useMemo, useState, useEffect } from 'react';
import { produce } from 'immer';
import type { Theme } from '@withjoy/joykit';
import { ThemeProvider } from '@withjoy/joykit/theme';
import type { GuestSiteTypographyOverrideProps, OverrideTypography } from './GuestSiteTypographyOverride.types';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import { toUnitless } from '@shared/joykit/packages/core/common/themes/shared/typography';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';

const overrideTypography = (
  currentTheme: Theme,
  override: OverrideTypography,
  defaultFontFamily: string | undefined,
  eventDesign: GuestSiteEvent['eventDesign'] | undefined,
): Theme => {
  if (!eventDesign) {
    return currentTheme;
  }

  return produce(currentTheme, draft => {
    override.forEach(({ category, variant }) => {
      const fontConfig = eventDesign.fonts?.find(font => font.category === category);
      const variantDraft = draft.typography.variants[variant];

      if (variantDraft) {
        // Apply font family override
        variantDraft.fontFamily = fontConfig?.font?.fontFamily || defaultFontFamily || variantDraft.fontFamily;

        // Only reset letter spacing when a font pack font is actually configured for this category
        if (fontConfig?.font?.fontFamily) {
          variantDraft.letterSpacing = '0em';
        }

        // Apply font size percentage override
        if (fontConfig?.sizePercent) {
          const baseFontSize = toUnitless(currentTheme.typography.variants[variant].fontSize);
          variantDraft.fontSize = `${baseFontSize * (fontConfig.sizePercent / 100)}rem`;
        }
      }
    });
  });
};

/**
 * GuestSiteTypographyOverride - Applies font pack category overrides to specific typography variants
 *
 * Applies local category-to-variant overrides while preserving the provider's effective theme.
 * Holds the previous local theme until the provider reports both a current base theme and all
 * category fonts available, preventing mixed-design output or a flash during font transitions.
 */
export const GuestSiteTypographyOverride: FC<GuestSiteTypographyOverrideProps> = props => {
  const { override, defaultFontFamily, children } = props;

  const { eventDesign, hasFont, loadedFonts, theme: baseTheme, isThemeCurrent } = useEventDesignThemingProvider();

  const categories = useMemo(() => override.map(o => o.category), [override]);
  const themeValue = useMemo(() => overrideTypography(baseTheme, override, defaultFontFamily, eventDesign), [baseTheme, defaultFontFamily, eventDesign, override]);
  const [theme, setTheme] = useState<Theme>(isThemeCurrent ? themeValue : baseTheme);

  // Only update theme when all category fonts are loaded
  useEffect(() => {
    if (eventDesign) {
      // `?? []`: partial-hydration guard so `.filter` can't throw — see MCP_PREVIEW.md#partial-hydration.
      const fontsForCategories = (eventDesign.fonts ?? [])
        .filter(font => categories.includes(font.category))
        .map(font => font.font?.fontFamily)
        .filter(Boolean) as string[];

      const unloadedFonts = fontsForCategories.filter(font => !hasFont(font));

      // A loaded font can publish before its matching provider theme; wait for both halves
      // to avoid combining the next design's typography with the previous design's base.
      if (isThemeCurrent && unloadedFonts.length === 0) {
        setTheme(themeValue);
      }
      // Otherwise: keep current theme to prevent FOUT
      // Old fonts remain in DOM (EventDesignThemingProvider accumulates fonts), so no flicker
    }
  }, [categories, eventDesign, hasFont, isThemeCurrent, loadedFonts, themeValue]);

  return eventDesign?.fonts ? <ThemeProvider theme={theme}>{children}</ThemeProvider> : <>{children}</>;
};
